import qs from 'qs'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { deleteDetergent, getAllDetergents, getDetergent, getDetergents } from '@/apis/detergents'
import { API_LIMIT } from '@/constants/api'
import useNotification from '@/hooks/useNotification'
import useQuery from '@/hooks/useQuery'
import useQueryPage from '@/hooks/useQueryPage'
import { Detergent } from '@/types'
import { GetAllDetergentsResponse } from '@/types/apis/detergents/GetAllDetergentsResponse'
import { GetDetergentResponse } from '@/types/apis/detergents/GetDetergentResponse'
import { DeleteIdCardRequest } from '@/types/apis/idCards'

export type PageQueryParams = {
  page?: number
}

export const useDetergents = () => {
  const currentPage = useQueryPage()
  const query = useQuery()
  const { t } = useTranslation()
  const openNotification = useNotification()
  const [page, setPage] = useState(currentPage)
  const [getDetergentsLoading, setGetDetergentsLoading] = useState(false)
  const [getDetergentsResponse, setDetergentsResponse] = useState<GetAllDetergentsResponse>()
  const [getDetergentDetailResponse, setDetergentDetailResponse] = useState<GetDetergentResponse>()
  const [detergents, setDetergents] = useState<Detergent[]>()
  const [deleteDetergentLoading, setDeleteDetergentLoading] = useState(false)

  const filterToQuery = (filter: { page?: number }): string => {
    const filterQuery: PageQueryParams = {}

    if (filter.page) {
      filterQuery.page = filter.page
    }

    return qs.stringify(filterQuery)
  }

  const getAllDetergent = useCallback(async () => {
    try {
      setGetDetergentsLoading(true)
      const res = await getDetergents()

      setDetergents(res)
    } finally {
      setGetDetergentsLoading(false)
    }
  }, [])

  const get = useCallback(async () => {
    try {
      setGetDetergentsLoading(true)
      const res = await getAllDetergents({ limit: API_LIMIT, page })

      setDetergentsResponse(res)
    } finally {
      setGetDetergentsLoading(false)
    }
  }, [page])

  const getById = useCallback(async (id: number) => {
    try {
      setGetDetergentsLoading(true)
      const res = await getDetergent({ id })

      setDetergentDetailResponse(res)
    } finally {
      setGetDetergentsLoading(false)
    }
  }, [])

  const del = useCallback(
    async ({ id }: DeleteIdCardRequest) => {
      try {
        setDeleteDetergentLoading(true)
        await deleteDetergent({ id })

        if (getDetergentsResponse) {
          const newMachineParts = getDetergentsResponse.items.filter((item) => item.id !== id)
          setDetergentsResponse({ ...getDetergentsResponse, ...newMachineParts })
        }
        openNotification({ type: 'success', description: t('components.pages.admins.common.delete.success') })
      } catch {
        openNotification({ type: 'error', description: t('components.pages.admins.common.delete.fail') })
      } finally {
        setDeleteDetergentLoading(false)
      }
    },
    [getDetergentsResponse, openNotification, t]
  )

  useEffect(() => {
    const page = query.get('page')
    setPage(page ? Number(page) : 1)
  }, [query])

  return {
    page,
    pageSize: API_LIMIT,
    getDetergents: get,
    getDetergentsResponse,
    getDetergentsLoading,
    deleteDetergent: del,
    deleteDetergentLoading,
    getDetergentDetail: getById,
    getDetergentDetailResponse,
    filterToQuery,
    getAllDetergent,
    detergents,
  }
}
